import { useEffect, useRef, useCallback } from 'react'
import type React from 'react'
import { isBasecampTodoNotesEventForThread } from '@/lib/open-basecamp-todo-notes-chat'
import { dispatchBasecampTodoRefreshFromNotesWrite } from '@/lib/basecamp-ingestion-events'
import { shouldHideJumpToBottom } from './chat-pane.utils'
import { resolveAddToChatTarget, flashReferenceChip } from './chat-pane.add-to-chat-target'
import {
  enqueueUpdateWorkSpecKickoff,
  shouldKickoffSeededUpdateWorkSpec,
} from '@/lib/update-work-spec-kickoff'
import {
  attachFillCommentChoice,
  setActiveTodoActionSession,
} from '@/lib/open-basecamp-todo-action-chat'
import { toast } from 'sonner'
import { isChatStreamTransportError } from '@/lib/chat-stream-error-display'
import { CHAT_STREAM_REPLY_KEPT_TOAST } from '@/lib/chat-stream-tail-recovery'
import type { Message, DocumentReference } from './chat-pane.types'
import { getConversation, fetchWithAuth } from '@/lib/api'
import { devLog } from '@/lib/dev-log'
import { getLocalCalendarDate, getClientTimezone } from '@/lib/utils'
import type { ContentEditableChatInputHandle } from '@/components/ui/contenteditable-chat-input'
import { flipIngestionConversationToStreaming, isIngestionThreadComplete } from '@/lib/ingestion-streaming-flip'
import { mapApiMessagesToChatMessages, completedFilesFromMessages } from '@/lib/chat-message-hydration'
import { upsertCompletedFile } from '@/lib/chat-completed-files'
import { isKnownGoneConversationId, markConversationIdGone, isPermanentlyGoneStatus } from '@/lib/conversation-load-guard'
import { hydrateConversationDraftScope, shouldReleaseAgentPanelForWorkspaceFilter } from '@/lib/chat-conversation-scope'
import type { OpenChatPromptDetail } from '@/lib/mermaid-chat'
import { useChatSurfaceRegistryOptional } from '@/hooks/use-chat-surface-registry'
import { useWorkspace } from '@/providers/workspace-provider'
import { peekThreadWorkspaceMeta } from '@/lib/thread-workspace-meta'

/**
 * Hook to load conversation history from sessionStorage on mount
 */
export function useConversationHistoryLoader(deps: {
  setStreamingMessages: (messages: Message[]) => void
  setStreamingConversationId: (id: string | null) => void
  lastLoadedScopePathsRef: React.MutableRefObject<string[] | null>
  /** Skip legacy session restore when a hosted thread surface owns the chat. */
  skip?: boolean
}) {
  const { setStreamingMessages, setStreamingConversationId, lastLoadedScopePathsRef, skip } = deps

  useEffect(() => {
    if (skip) return

    const loadConversationHistory = async () => {
      const savedConversationId = sessionStorage.getItem('flownotes_current_conversation_id')

      if (savedConversationId && isKnownGoneConversationId(savedConversationId)) {
        // Some other surface/mount already confirmed this id 404/410'd this session (see
        // conversation-load-guard.ts) - this hook re-runs on every mount (a ref-based guard alone
        // does not survive remounts), so without this check, switching chat tabs/threads
        // repeatedly re-triggers a fetch that is guaranteed to fail again.
        sessionStorage.removeItem('flownotes_current_conversation_id')
        return
      }

      if (savedConversationId) {
        try {
          devLog('[ChatPane] Restoring conversation from session:', savedConversationId)
          const result = await getConversation(savedConversationId, true)

          if (
            result.conversation &&
            Array.isArray(result.conversation.messages)
          ) {
            const chatMessages: Message[] = result.conversation.messages.map((msg: any) => ({
              role: msg.role as "user" | "assistant",
              content: msg.content,
              messageId: msg.id != null ? String(msg.id) : undefined,
              references: (msg.references as DocumentReference[] | undefined) ?? undefined,
              skill: msg.metadata?.skill ?? undefined,
              timestamp: msg.created_at ? new Date(msg.created_at) : undefined,
              editOf: msg.edit_of != null ? String(msg.edit_of) : undefined,
            }))

            setStreamingMessages(chatMessages)
            setStreamingConversationId(savedConversationId)
            const scopePaths = result.conversation.workspace_scope
            lastLoadedScopePathsRef.current =
              scopePaths && Array.isArray(scopePaths) && scopePaths.length > 0 ? scopePaths : null
            devLog('[ChatPane] Restored', chatMessages.length, 'messages')
          }
        } catch (error) {
          console.error('[ChatPane] Failed to restore conversation:', error)
          sessionStorage.removeItem('flownotes_current_conversation_id')
          const status = (error as { status?: number } | undefined)?.status
          if (isPermanentlyGoneStatus(status)) {
            // Shared across every surface/mount - stops the hosted-thread load path
            // (chat-pane.handlers.ts) or another surface's own mount-restore from retrying it too.
            markConversationIdGone(savedConversationId)
          }
        }
      }
    }

    loadConversationHistory()
  }, [skip, setStreamingMessages, setStreamingConversationId, lastLoadedScopePathsRef])
}

/**
 * Hook to apply workspace scope after workspaces load
 */
export function useWorkspaceScopeApplier(deps: {
  workspaces: Array<{ id: string; contentRoot?: string | null }>
  setConversationScope: (scope: string[] | null) => void
  lastLoadedScopePathsRef: React.MutableRefObject<string[] | null>
  userExplicitlySetScopeRef?: React.MutableRefObject<boolean>
}) {
  const { workspaces, setConversationScope, lastLoadedScopePathsRef, userExplicitlySetScopeRef } = deps

  useEffect(() => {
    const scopePaths = lastLoadedScopePathsRef.current
    if (scopePaths == null || !Array.isArray(workspaces) || workspaces.length === 0) return
    const ids = hydrateConversationDraftScope({
      scopePaths,
      workspaces,
    })
    setConversationScope(ids)
    if (userExplicitlySetScopeRef && ids && ids.length > 0) {
      userExplicitlySetScopeRef.current = true
    }
    lastLoadedScopePathsRef.current = null
  }, [workspaces, setConversationScope, lastLoadedScopePathsRef, userExplicitlySetScopeRef])
}

/**
 * Hook to persist conversation ID to sessionStorage
 */
export function useConversationIdPersistence(streamingConversationId: string | null) {
  useEffect(() => {
    if (streamingConversationId) {
      sessionStorage.setItem('flownotes_current_conversation_id', streamingConversationId)
    }
  }, [streamingConversationId])
}

/**
 * Hook to listen for open-chat-conversation events
 */
export function useOpenConversationListener(deps: {
  collapsed: boolean
  onToggleCollapse?: () => void
  setLoadingConversation: (loading: boolean) => void
  setLegacyMessages: (messages: Message[]) => void
  setStreamingMessages: (messages: Message[]) => void
  setCurrentConversationId: (id: string | null) => void
  setStreamingConversationId: (id: string | null) => void
  setThinkingModeActive: (active: boolean) => void
  setThinkingOperations: (ops: any[]) => void
  setCompletedFiles: (files: any[]) => void
  setCurrentTask: (task: any) => void
  startPolling: (id: string, fastMode?: boolean) => void
  stopPolling: () => void
  messagesContainerRef: React.RefObject<HTMLDivElement | null>
  surfaceVariant?: 'compact' | 'comfortable' | 'editor'
  hostedThreadId?: string
  onIngestionBootstrapComplete?: (threadId: string) => void
}) {
  const {
    collapsed,
    onToggleCollapse,
    setLoadingConversation,
    setLegacyMessages,
    setStreamingMessages,
    setCurrentConversationId,
    setStreamingConversationId,
    setThinkingModeActive,
    setThinkingOperations,
    setCompletedFiles,
    setCurrentTask,
    startPolling,
    stopPolling,
    messagesContainerRef,
    surfaceVariant,
    hostedThreadId,
    onIngestionBootstrapComplete,
  } = deps

  useEffect(() => {
    const handleOpenConversation = async (event: CustomEvent) => {
      const {
        conversationId,
        targetSurface,
        targetThreadId,
      } = event.detail ?? {}

      if (targetSurface || targetThreadId) {
        const isAgentPanel = surfaceVariant === 'compact'
        const matchesSurface =
          !targetSurface
          || (targetSurface === 'agent-panel' && isAgentPanel)
          || (targetSurface === 'editor' && surfaceVariant === 'editor')
        const matchesThread =
          !targetThreadId
          || (hostedThreadId != null && targetThreadId === hostedThreadId)
        if (!matchesSurface || !matchesThread) {
          return
        }
      }

      devLog('[ChatPane] Loading conversation:', conversationId);

      // Shared guard (see conversation-load-guard.ts): another surface/mount already confirmed
      // this id 404/410'd this session - don't add another doomed fetch to the pile.
      if (isKnownGoneConversationId(conversationId)) {
        if (targetThreadId && onIngestionBootstrapComplete) {
          onIngestionBootstrapComplete(targetThreadId)
        }
        return
      }

      setLoadingConversation(true);

      try {
        // Fetch conversation with messages
        const result = await getConversation(conversationId, true);
        const conversation = result.conversation;

        const meta = conversation.metadata || {};
        const isIngestion =
          meta.type === 'basecamp-ingestion' || meta.type === 'ingestion';

        // Completed ingestion: single streaming path + active ingested todo (no refinement API).
        if (isIngestion && isIngestionThreadComplete(conversation)) {
          await flipIngestionConversationToStreaming({
            stopPolling,
            setCurrentConversationId,
            setStreamingConversationId,
            setStreamingMessages,
            setLegacyMessages,
            setThinkingModeActive,
            conversationId,
            conversation,
          })
        } else if (isIngestion) {
          const chatMessages: Message[] = (conversation.messages || []).map((msg: any) => ({
            role: msg.role as "user" | "assistant",
            content: msg.content,
            messageId: msg.id != null ? String(msg.id) : undefined,
            skill: msg.metadata?.skill ?? undefined,
            references: msg.references as DocumentReference[] | undefined,
            editOf: msg.edit_of != null ? String(msg.edit_of) : undefined,
          }))
          setLegacyMessages(chatMessages);
          setCurrentConversationId(conversationId);
          setStreamingConversationId(null);

          const isIngestionInProgress = !isIngestionThreadComplete(conversation);

          if (isIngestionInProgress) {
            setThinkingModeActive(true);
            setThinkingOperations([]);
            setCompletedFiles([]);
            setCurrentTask(null);
          }

          if (isIngestionInProgress && conversation.status === 'active') {
            devLog('[ChatPane] Starting polling - Type:', conversation.metadata?.type, 'Fast mode: true');
            startPolling(conversationId, true);
          }
        } else {
          const chatMessages = mapApiMessagesToChatMessages(conversation.messages || [])
          setStreamingMessages(chatMessages)
          setCompletedFiles(completedFilesFromMessages(chatMessages))
          setStreamingConversationId(conversationId)
          setCurrentConversationId(null)
          setLegacyMessages([])

          if (
            shouldKickoffSeededUpdateWorkSpec({
              source: meta.source,
              messages: chatMessages,
            })
          ) {
            enqueueUpdateWorkSpecKickoff({
              targetConversationId: conversationId,
              targetThreadId,
              documentId: meta.document_id,
              skillId: meta.skill_id || 'basecamp-todo',
            })
          }
        }

        // Expand chat panel if collapsed
        if (collapsed && onToggleCollapse) {
          onToggleCollapse();
        }

        // Scroll to bottom only when user is near bottom (read from DOM)
        setTimeout(() => {
          const el = messagesContainerRef.current
          if (el) {
            const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
            if (distanceFromBottom <= 100) {
              el.scrollTop = el.scrollHeight
            }
          }
        }, 100);

      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return
        }
        console.error('[ChatPane] Failed to load conversation:', error);
        toast.error('Failed to load conversation', {
          description: error instanceof Error ? error.message : 'Unknown error'
        });
        const status = (error as { status?: number } | undefined)?.status
        if (isPermanentlyGoneStatus(status)) {
          markConversationIdGone(conversationId)
        }
      } finally {
        setLoadingConversation(false);
        if (targetThreadId && onIngestionBootstrapComplete) {
          onIngestionBootstrapComplete(targetThreadId)
        }
      }
    };

    window.addEventListener('open-chat-conversation', handleOpenConversation as unknown as EventListener);

    return () => {
      window.removeEventListener('open-chat-conversation', handleOpenConversation as unknown as EventListener);
    };
  }, [
    collapsed,
    onToggleCollapse,
    setLoadingConversation,
    setLegacyMessages,
    setStreamingMessages,
    setCurrentConversationId,
    setStreamingConversationId,
    setThinkingModeActive,
    setThinkingOperations,
    setCompletedFiles,
    setCurrentTask,
    startPolling,
    stopPolling,
    messagesContainerRef,
    surfaceVariant,
    hostedThreadId,
    onIngestionBootstrapComplete,
  ])
}

export function useChatEventListeners(deps: {
  collapsed: boolean
  onToggleCollapse?: () => void
  contentEditableInputRef: React.RefObject<ContentEditableChatInputHandle | null>
  setStreamingInput: (input: string) => void
  setCurrentConversationId: (id: string | null) => void
  setLegacyMessages: (messages: Message[]) => void
  setStreamingMessages: (messages: Message[]) => void
}) {
  const {
    collapsed,
    onToggleCollapse,
    contentEditableInputRef,
    setStreamingInput,
    setCurrentConversationId,
    setLegacyMessages,
    setStreamingMessages,
  } = deps
  const registry = useChatSurfaceRegistryOptional()
  const { activeWorkspaceIds } = useWorkspace()

  // Listen for add-to-chat events
  useEffect(() => {
    const handleAddToChat = (event: CustomEvent) => {
      const data = event.detail;
      if (!data?.id) return;

      const hosted = registry?.agentPanelThreadId ?? null
      const hostedMeta = hosted ? peekThreadWorkspaceMeta(hosted) : null
      const hostedOutOfScope = Boolean(
        hosted &&
          !hosted.startsWith('new-') &&
          shouldReleaseAgentPanelForWorkspaceFilter(hostedMeta, activeWorkspaceIds),
      )
      // Panel closed over a saved chat, or that chat is out of scope: new chat, not the archive.
      // A blank new-* composer is reused so the chip lands next to what the user already pasted.
      const target = resolveAddToChatTarget({
        collapsed,
        hostedThreadId: hosted,
        hostedOutOfScope,
      })
      if (target.createNewChat) {
        registry?.createNewChat({ forceAgentPanel: true, preferNew: target.preferNew })
      }

      const doInsert = () => {
        const input = contentEditableInputRef.current;
        if (!input) return;
        const isFolder =
          data.kind === 'folder' ||
          data.documentType === 'folder' ||
          data.type === 'folder'
        const lineStart =
          typeof data.lineStart === 'number' ? data.lineStart : undefined
        const lineEnd =
          typeof data.lineEnd === 'number' ? data.lineEnd : undefined
        const idAttr = String(data.id ?? '')
        const editorRoot = input.editorRef?.current
        let existing: NodeListOf<Element> | undefined
        if (editorRoot && idAttr) {
          try {
            existing = editorRoot.querySelectorAll(
              `[data-reference-id="${CSS.escape(idAttr)}"]`
            )
          } catch {
            existing = editorRoot.querySelectorAll(
              `[data-reference-id="${idAttr.replace(/"/g, '')}"]`
            )
          }
        }
        if (existing != null && existing.length > 0 && lineStart === undefined) {
          flashReferenceChip(existing[0]!)
          return
        }
        if (existing != null && existing.length > 0 && lineStart !== undefined) {
          const match = Array.from(existing).find((el) => {
            const start = el.getAttribute('data-line-start')
            const end = el.getAttribute('data-line-end')
            return (
              start === String(lineStart) &&
              (lineEnd === undefined || end === String(lineEnd))
            )
          })
          if (match) {
            flashReferenceChip(match)
            return
          }
        }
        input.insertReference({
          id: data.id,
          title: data.title || data.name || (isFolder ? 'Folder' : 'Document'),
          type: isFolder ? 'folder' : (data.documentType || 'note'),
          kind: isFolder ? 'folder' : undefined,
          path: data.path || (isFolder ? data.id : data.path),
          lineStart,
          lineEnd,
          excerpt: typeof data.excerpt === 'string' ? data.excerpt : undefined,
          excerptTail: typeof data.excerptTail === 'string' && data.excerptTail ? data.excerptTail : undefined,
        });
      };

      if (collapsed) {
        onToggleCollapse?.();
        // Defer until after the panel animation / ref mount (~150ms)
        setTimeout(doInsert, 150);
      } else {
        doInsert();
      }
    };
    window.addEventListener('add-to-chat', handleAddToChat as unknown as EventListener);
    return () => window.removeEventListener('add-to-chat', handleAddToChat as unknown as EventListener);
  }, [collapsed, onToggleCollapse, contentEditableInputRef, registry, activeWorkspaceIds]);

  // Listen for chat-set-draft events
  useEffect(() => {
    const handleSetDraft = (event: CustomEvent) => {
      const { text } = event.detail;
      if (!text) return;
      if (collapsed) onToggleCollapse?.();
      setStreamingInput(text);
    };
    window.addEventListener('chat-set-draft', handleSetDraft as unknown as EventListener);
    return () => window.removeEventListener('chat-set-draft', handleSetDraft as unknown as EventListener);
  }, [collapsed, onToggleCollapse, setStreamingInput]);

  // Listen for open-chat-prompt events
  useEffect(() => {
    const handleOpenChatPrompt = (event: CustomEvent<OpenChatPromptDetail>) => {
      const { query, newThread, documentReference } = event.detail
      if (!query) return

      const applyPrompt = () => {
        if (newThread) {
          setCurrentConversationId(null)
          setLegacyMessages([])
          setStreamingMessages([])
        }

        const input = contentEditableInputRef.current
        if (documentReference?.id && input?.setComposerWithReference) {
          input.setComposerWithReference(
            {
              id: documentReference.id,
              title: documentReference.title || 'Document',
              type: (documentReference.type as DocumentReference['type']) || 'note',
              path: documentReference.path || documentReference.id,
            },
            query,
          )
          setStreamingInput(query)
          input.focus()
          return
        }

        setStreamingInput(query)
      }

      if (collapsed) {
        onToggleCollapse?.()
        setTimeout(applyPrompt, 150)
      } else {
        applyPrompt()
      }
    }
    window.addEventListener('flownotes:open-chat-prompt', handleOpenChatPrompt as unknown as EventListener)
    return () => window.removeEventListener('flownotes:open-chat-prompt', handleOpenChatPrompt as unknown as EventListener)
  }, [
    collapsed,
    onToggleCollapse,
    setCurrentConversationId,
    setLegacyMessages,
    setStreamingMessages,
    setStreamingInput,
    contentEditableInputRef,
  ])
}

/**
 * Hook to listen for Basecamp notes generation and thinking mode events
 */
export function useBasecampNotesListeners(deps: {
  hostedThreadId?: string
  collapsed: boolean
  onToggleCollapse?: () => void
  setThinkingModeActive: (active: boolean) => void
  setThinkingOperations: (ops: any[] | ((prev: any[]) => any[])) => void
  setCompletedFiles: (files: any[] | ((prev: any[]) => any[])) => void
  setCurrentTask: (task: any) => void
  setPendingBasecampDraft: (draft: { todoId: string; html: string; todoUrl: string; title?: string } | null) => void
  messagesContainerRef?: React.RefObject<HTMLDivElement | null>
}) {
  const {
    hostedThreadId,
    collapsed,
    onToggleCollapse,
    setThinkingModeActive,
    setThinkingOperations,
    setCompletedFiles,
    setCurrentTask,
    setPendingBasecampDraft,
    messagesContainerRef,
  } = deps

  const routeBasecampNotesEvent = (eventThreadId: string | undefined): 'handle' | 'ignore' | 'clear' => {
    // Streaming SSE may set draft directly; window events without threadId target this pane only.
    if (!eventThreadId) {
      return hostedThreadId ? 'ignore' : 'handle'
    }
    if (isBasecampTodoNotesEventForThread(eventThreadId, hostedThreadId)) return 'handle'
    if (hostedThreadId) return 'clear'
    return 'ignore'
  }

  useEffect(() => {
    const handleBasecampNotesStart = (event: CustomEvent) => {
      const { todoId, threadId } = event.detail ?? {}
      const route = routeBasecampNotesEvent(threadId)
      if (route === 'ignore') return
      if (route === 'clear') {
        setPendingBasecampDraft(null)
        setThinkingModeActive(false)
        return
      }

      devLog('[ChatPane] Basecamp notes generation started for todo:', todoId);
      
      // Expand chat pane if collapsed
      if (collapsed && onToggleCollapse) {
        onToggleCollapse();
      }
      
      // Activate thinking mode
      setThinkingModeActive(true);
      setThinkingOperations([]);
      setCompletedFiles([]);
      setCurrentTask(null);
    };

    const handleThinkingModeEvent = (event: CustomEvent) => {
      const data = event.detail ?? {}
      const route = routeBasecampNotesEvent(data.threadId as string | undefined)
      if (route === 'ignore') return
      if (route === 'clear') {
        setPendingBasecampDraft(null)
        setThinkingModeActive(false)
        return
      }

      devLog('[ChatPane] Thinking mode event received:', data.type);

      // Handle different event types
      if (data.type === 'thinking_start') {
        // Expand chat pane if collapsed
        if (collapsed && onToggleCollapse) {
          onToggleCollapse();
        }
        setThinkingModeActive(true);
        setThinkingOperations([]);
        // Keep session Files bar across turns (createDocument then later edits).
        setCurrentTask(null);
      } else if (data.type === 'thinking_operation') {
        setThinkingModeActive(true);
        setThinkingOperations(prev => [...prev, data.operation]);
      } else if (data.type === 'thinking_operation_update') {
        setThinkingOperations(prev =>
          prev.map(op =>
            op.id === data.operationId
              ? { ...op, ...data.updates }
              : op
          )
        );
      } else if (data.type === 'thinking_file_complete') {
        setCompletedFiles(prev => upsertCompletedFile(prev, data.file));
      } else if (data.type === 'thinking_complete') {
        setThinkingModeActive(false);
      } else if (data.type === 'error') {
        setThinkingModeActive(false);
        toast.error('Basecamp notes generation failed', {
          description: data.error || data.message
        });
      }
    };

    const handleBasecampNotesComplete = (event: CustomEvent) => {
      const { threadId } = event.detail ?? {}
      const route = routeBasecampNotesEvent(threadId)
      if (route !== 'handle') return

      devLog('[ChatPane] Basecamp notes generation complete:', event.detail?.result);
      setThinkingModeActive(false);
    };

    const handleBasecampNotesError = (event: CustomEvent) => {
      const { threadId } = event.detail ?? {}
      const route = routeBasecampNotesEvent(threadId)
      if (route !== 'handle') return

      const { error } = event.detail ?? {}
      console.error('[ChatPane] Basecamp notes generation error:', error);
      setThinkingModeActive(false);
    };

    const handleBasecampDraftReady = (event: CustomEvent) => {
      const { todoId, html, todoUrl, title, threadId } = event.detail ?? {}
      const route = routeBasecampNotesEvent(threadId)
      if (route !== 'handle') return

      devLog('[ChatPane] Basecamp draft ready for approval:', todoId);
      if (collapsed && onToggleCollapse) onToggleCollapse();
      setThinkingModeActive(false);
      setPendingBasecampDraft({ todoId, html, todoUrl, title });
      // Scroll to bottom so the draft is visible
      setTimeout(() => {
        if (messagesContainerRef?.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }, 50);
    };

    window.addEventListener('basecamp-notes-generation-start', handleBasecampNotesStart as unknown as EventListener);
    window.addEventListener('thinking-mode-event', handleThinkingModeEvent as unknown as EventListener);
    window.addEventListener('basecamp-notes-generation-complete', handleBasecampNotesComplete as unknown as EventListener);
    window.addEventListener('basecamp-notes-generation-error', handleBasecampNotesError as unknown as EventListener);
    window.addEventListener('basecamp-draft-ready', handleBasecampDraftReady as unknown as EventListener);

    return () => {
      window.removeEventListener('basecamp-notes-generation-start', handleBasecampNotesStart as unknown as EventListener);
      window.removeEventListener('thinking-mode-event', handleThinkingModeEvent as unknown as EventListener);
      window.removeEventListener('basecamp-notes-generation-complete', handleBasecampNotesComplete as unknown as EventListener);
      window.removeEventListener('basecamp-notes-generation-error', handleBasecampNotesError as unknown as EventListener);
      window.removeEventListener('basecamp-draft-ready', handleBasecampDraftReady as unknown as EventListener);
    };
  }, [hostedThreadId, collapsed, onToggleCollapse, setThinkingModeActive, setThinkingOperations, setCompletedFiles, setCurrentTask, setPendingBasecampDraft, messagesContainerRef]);
}

/**
 * Hook to listen for triage analyze requests
 */
export function useTriageAnalyzeListener(deps: {
  collapsed: boolean
  onToggleCollapse?: () => void
  setCurrentConversationId: (id: string | null) => void
  setStreamingMessages: (messages: Message[] | ((prev: Message[]) => Message[])) => void
  setThinkingModeActive: (active: boolean) => void
  setThinkingOperations: (ops: any[] | ((prev: any[]) => any[])) => void
  setIsStreaming: (streaming: boolean) => void
  setIsTyping: (typing: boolean) => void
  selectedProvider: string
  selectedModel: string
  workspaceScopePaths: string[]
  abortControllerRef: React.MutableRefObject<AbortController | null>
}) {
  const {
    collapsed,
    onToggleCollapse,
    setCurrentConversationId,
    setStreamingMessages,
    setThinkingModeActive,
    setThinkingOperations,
    setIsStreaming,
    setIsTyping,
    selectedProvider,
    selectedModel,
    workspaceScopePaths,
    abortControllerRef,
  } = deps

  const workspaceScopePathsKey = workspaceScopePaths.join(',')

  useEffect(() => {
    const handleTriageAnalyze = async (event: CustomEvent<{
      todoUrl: string;
      todoTitle: string;
      todoId?: string;
      projectId?: string;
      projectName?: string;
      dueOn?: string;
      daysOverdue?: number;
    }>) => {
      const { todoUrl, todoTitle, todoId, projectId, projectName, dueOn, daysOverdue } = event.detail || {};
      if (!todoUrl || !todoTitle) return;

      if (collapsed && onToggleCollapse) onToggleCollapse();
      setCurrentConversationId(null);

      const overdueLine = daysOverdue != null && daysOverdue > 0
        ? `Days overdue: ${daysOverdue}`
        : dueOn ? `Due: ${dueOn}` : '';
      const injectedMessage = [
        `[TRIAGE_ANALYZE]`,
        `Todo: "${todoTitle}"`,
        todoId ? `Todo ID: ${todoId}` : null,
        projectId ? `Project ID: ${projectId}` : null,
        projectName ? `Project: ${projectName}` : null,
        overdueLine || null,
        `Source URL: ${todoUrl}`,
        ``,
        `Search instructions:`,
        `1. Call fetchBasecampTodo with todoId and projectId to get full details and comments.`,
        `2. Call searchDocuments with the Todo ID ("${todoId}") to find any existing work spec.`,
        `3. Call searchDocuments again with specific keywords from the todo title and description — look for related architecture docs, past similar work, or existing specs for the same client/project.`,
        `4. Follow the decision tree in your instructions based on what you find.`,
      ].filter(Boolean).join('\n');

      const userMessage: Message = {
        role: 'user',
        content: `Analyze "${todoTitle}" for triage`,
        timestamp: new Date(),
      };

      setStreamingMessages([userMessage]);
      setThinkingModeActive(true);
      setThinkingOperations([]);
      setIsStreaming(true);
      setIsTyping(true);

      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      let assistantMessage = '';

      try {
        const res = await fetchWithAuth(`/api/chat-stream`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages: [{ role: 'user', content: injectedMessage }],
            clientLocalDate: getLocalCalendarDate(),
            clientTimezone: getClientTimezone(),
            references: [],
            activeDocumentId: null,
            viewContext: 'triage_analyze',
            workspaceScope: workspaceScopePaths,
            provider: selectedProvider,
            model: selectedModel,
          }),
          signal: abortController.signal,
        });

        if (!res.ok) throw new Error(`Analysis failed: HTTP ${res.status}`);

        const reader = res.body?.getReader();
        const decoder = new TextDecoder();
        if (!reader) throw new Error('No response body');

        setStreamingMessages(prev => [...prev, {
          role: 'assistant',
          content: '',
          timestamp: new Date(),
          toolResults: [],
        }]);

        let buffer = '';

        readerLoop: while (true) {
          if (abortController.signal.aborted) { reader.cancel(); break; }
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split('\n\n');
          buffer = events.pop() || '';

          for (const ev of events) {
            if (!ev.trim()) continue;
            const lines = ev.split('\n');
            for (const line of lines) {
              if (!line.startsWith('data: ')) continue;
              const data = line.slice(6);
              if (data === '[DONE]') {
                void reader.cancel().catch(() => {});
                break readerLoop;
              }
              try {
                const parsed = JSON.parse(data);

                if (parsed.type === 'thinking_start') {
                  setThinkingModeActive(true);
                  setThinkingOperations([]);
                } else if (parsed.type === 'thinking_operation') {
                  setThinkingModeActive(true);
                  setThinkingOperations(prev => [...prev, parsed.operation]);
                } else if (parsed.type === 'thinking_operation_update') {
                  setThinkingOperations(prev =>
                    prev.map(op => op.id === parsed.operationId ? { ...op, ...parsed.updates } : op)
                  );
                } else if (parsed.type === 'thinking_complete') {
                  setThinkingModeActive(false);
                } else if (parsed.type === 'followup_start') {
                  setIsTyping(true);
                }

                if (parsed.type === 'text' && parsed.content) {
                  setIsTyping(false);
                  assistantMessage += parsed.content;
                  setStreamingMessages(prev => {
                    const next = [...prev];
                    const last = next[next.length - 1];
                    if (last?.role === 'assistant') {
                      next[next.length - 1] = { ...last, content: assistantMessage };
                    }
                    return next;
                  });
                }

                if (parsed.type === 'tool_call') {
                  const toolMsg: Message = {
                    role: 'tool',
                    content: parsed.result?.error ? (parsed.result.error as string) : '',
                    toolName: parsed.tool,
                    toolResult: parsed.result,
                    ...(parsed.tool === 'createDocument' && parsed.args?.content != null && {
                      outputContent: parsed.args.content
                    }),
                    toolResults: [{ tool: parsed.tool, args: parsed.args, result: parsed.result }],
                    timestamp: new Date(),
                    ...(parsed.batch_id && {
                      batchId: parsed.batch_id,
                      batchIndex: parsed.batch_index,
                      batchSize: parsed.batch_size,
                    }),
                  };
                  setStreamingMessages(prev => {
                    const next = [...prev];
                    const lastIdx = next.length - 1;
                    if (next[lastIdx]?.role === 'assistant') {
                      next.splice(lastIdx, 0, toolMsg);
                    } else {
                      next.push(toolMsg);
                    }
                    return next;
                  });

                  if (
                    parsed.tool === 'updateBasecampTodoNotes' &&
                    parsed.result?.success
                  ) {
                    window.dispatchEvent(new CustomEvent('triage-due-date-updated', {
                      detail: { todoUrl: parsed.args?.todoUrl }
                    }));
                    dispatchBasecampTodoRefreshFromNotesWrite({
                      todoId: parsed.result?.todoId ?? parsed.result?.todo?.id,
                      todoUrl: parsed.args?.todoUrl ?? parsed.result?.todoUrl,
                    });
                  }
                }

                if (parsed.type === 'triage_work_spec' && parsed.doc) {
                  setStreamingMessages(prev => {
                    const next = [...prev];
                    const last = next[next.length - 1];
                    if (last?.role === 'assistant') {
                      next[next.length - 1] = {
                        ...last,
                        references: [
                          ...(last.references || []),
                          {
                            id: parsed.doc.id,
                            title: parsed.doc.title,
                            type: (parsed.doc.type || 'work_spec') as DocumentReference['type'],
                            path: parsed.doc.path,
                          }
                        ]
                      };
                    }
                    return next;
                  });
                }

                if ((parsed.type === 'suggest_actions' || parsed.type === 'action_choices') && parsed.choices) {
                  setStreamingMessages(prev => {
                    const next = [...prev];
                    const last = next[next.length - 1];
                    if (last?.role === 'assistant') {
                      next[next.length - 1] = { ...last, actionChoices: parsed.choices };
                    }
                    return next;
                  });
                }
              } catch (_) { /* ignore parse errors */ }
            }
          }
        }
      } catch (err) {
        if (abortController.signal.aborted) return;
        if (isChatStreamTransportError(err) && assistantMessage.trim()) {
          toast.message(CHAT_STREAM_REPLY_KEPT_TOAST.title, {
            description: CHAT_STREAM_REPLY_KEPT_TOAST.description,
          });
          return;
        }
        toast.error(err instanceof Error ? err.message : 'Analysis failed');
        setStreamingMessages(prev => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last?.role === 'assistant') {
            next[next.length - 1] = { ...last, content: `Analysis failed: ${err instanceof Error ? err.message : 'Unknown error'}` };
          }
          return next;
        });
      } finally {
        setThinkingModeActive(false);
        setIsStreaming(false);
        setIsTyping(false);
      }
    };

    window.addEventListener('triage-analyze-request', handleTriageAnalyze as unknown as EventListener);
    return () => window.removeEventListener('triage-analyze-request', handleTriageAnalyze as unknown as EventListener);
  }, [collapsed, onToggleCollapse, setCurrentConversationId, setStreamingMessages, setThinkingModeActive, setThinkingOperations, setIsStreaming, setIsTyping, selectedProvider, selectedModel, workspaceScopePathsKey, abortControllerRef]);
}

export type TodoActionChatRequestDetail = {
  kind: 'draft_comment' | 'find_context'
  threadId: string
  todoUrl: string
  todoTitle: string
  todoId?: string
  projectId?: string
  projectName?: string
  workSpecPath?: string
  assigneeNames?: string
  dueOn?: string
}

/** Pending until the agent-panel ChatPane mounts with matching hostedThreadId. */
let pendingTodoActionChat: TodoActionChatRequestDetail | null = null

/**
 * Draft comment / find-context ACTIONS → dedicated agent-panel chat stream.
 * draft_comment also fills the Maater Comments form (no auto-post).
 */
export function useTodoActionChatListener(deps: {
  hostedThreadId?: string
  collapsed: boolean
  onToggleCollapse?: () => void
  setStreamingMessages: (messages: Message[] | ((prev: Message[]) => Message[])) => void
  setThinkingModeActive: (active: boolean) => void
  setThinkingOperations: (ops: any[] | ((prev: any[]) => any[])) => void
  setIsStreaming: (streaming: boolean) => void
  setIsTyping: (typing: boolean) => void
  selectedProvider: string
  selectedModel: string
  workspaceScopePaths: string[]
  abortControllerRef: React.MutableRefObject<AbortController | null>
}) {
  const {
    hostedThreadId,
    collapsed,
    onToggleCollapse,
    setStreamingMessages,
    setThinkingModeActive,
    setThinkingOperations,
    setIsStreaming,
    setIsTyping,
    selectedProvider,
    selectedModel,
    workspaceScopePaths,
    abortControllerRef,
  } = deps

  const workspaceScopePathsKey = workspaceScopePaths.join(',')
  const hostedThreadIdRef = useRef(hostedThreadId)
  hostedThreadIdRef.current = hostedThreadId

  useEffect(() => {
    const runTodoActionChat = async (detail: TodoActionChatRequestDetail) => {
      if (!detail?.kind || !detail.todoUrl || !detail.todoTitle || !detail.threadId) return
      if (hostedThreadIdRef.current !== detail.threadId) return

      if (collapsed && onToggleCollapse) onToggleCollapse()

      const {
        kind,
        todoUrl,
        todoTitle,
        todoId,
        projectId,
        projectName,
        workSpecPath,
        assigneeNames,
        dueOn,
      } = detail

      if (todoId) {
        setActiveTodoActionSession({
          threadId: detail.threadId,
          kind,
          todoId: String(todoId),
        })
      }

      const visibleUserMessage =
        kind === 'draft_comment'
          ? `Shape a Basecamp comment for "${todoTitle}"`
          : `Find more context for "${todoTitle}"`

      const injectedMessage =
        kind === 'draft_comment'
          ? [
              `[DRAFT_BASECAMP_COMMENT]`,
              `Todo: "${todoTitle}"`,
              todoId ? `Todo ID: ${todoId}` : null,
              projectId ? `Project ID: ${projectId}` : null,
              projectName ? `Project: ${projectName}` : null,
              assigneeNames ? `Assignees: ${assigneeNames}` : null,
              dueOn ? `Due: ${dueOn}` : null,
              workSpecPath ? `Work spec path: ${workSpecPath}` : null,
              `Source URL: ${todoUrl}`,
              ``,
              `You are shaping a Basecamp comment in Plan mode. Do not post.`,
              `1. Read fetchBasecampTodo / searchDocuments if needed (read-only).`,
              `2. If tone, audience, or intent is ambiguous, call askQuestion first (check-in vs chase vs status; internal vs client; short vs detailed). Wait for the selection.`,
              `3. Then propose the comment. Put ONLY the postable body in a fenced \`\`\`comment block. Prose outside the fence is for the user, not for posting.`,
              `4. Do not claim the comment was added to the form. The user will click "Add to comments form".`,
            ]
              .filter(Boolean)
              .join('\n')
          : [
              `[FIND_TODO_CONTEXT]`,
              `Todo: "${todoTitle}"`,
              todoId ? `Todo ID: ${todoId}` : null,
              projectId ? `Project ID: ${projectId}` : null,
              projectName ? `Project: ${projectName}` : null,
              workSpecPath ? `Known work spec: ${workSpecPath}` : null,
              `Source URL: ${todoUrl}`,
              ``,
              `You are in Plan mode. If search scope is ambiguous, call askQuestion first.`,
              `1. Call searchDocuments / getTodoContext with the Todo ID ("${todoId}") for the work spec.`,
              `2. Search again with keywords from the title for related docs, past similar work, and client/project context.`,
              `3. Optionally fetchBasecampTodo for description/comments if needed.`,
              `4. Summarize what you found with document titles/paths and how they relate. Suggest next steps if useful.`,
            ]
              .filter(Boolean)
              .join('\n')

      const viewContext = kind === 'draft_comment' ? 'draft_basecamp_comment' : 'find_todo_context'

      setStreamingMessages([
        { role: 'user', content: visibleUserMessage, timestamp: new Date() },
      ])
      setThinkingModeActive(true)
      setThinkingOperations([])
      setIsStreaming(true)
      setIsTyping(true)

      const abortController = new AbortController()
      abortControllerRef.current = abortController
      let assistantMessage = ''

      try {
        const res = await fetchWithAuth(`/api/chat-stream`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages: [{ role: 'user', content: injectedMessage }],
            clientLocalDate: getLocalCalendarDate(),
            clientTimezone: getClientTimezone(),
            references: [],
            activeDocumentId: null,
            viewContext,
            workspaceScope: workspaceScopePaths,
            provider: selectedProvider,
            model: selectedModel,
            mode: 'plan',
          }),
          signal: abortController.signal,
        })

        if (!res.ok) throw new Error(`Request failed: HTTP ${res.status}`)

        const reader = res.body?.getReader()
        const decoder = new TextDecoder()
        if (!reader) throw new Error('No response body')

        setStreamingMessages((prev) => [
          ...prev,
          { role: 'assistant', content: '', timestamp: new Date(), toolResults: [] },
        ])

        let buffer = ''

        readerLoop: while (true) {
          if (abortController.signal.aborted) {
            reader.cancel()
            break
          }
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const events = buffer.split('\n\n')
          buffer = events.pop() || ''

          for (const ev of events) {
            if (!ev.trim()) continue
            for (const line of ev.split('\n')) {
              if (!line.startsWith('data: ')) continue
              const data = line.slice(6)
              if (data === '[DONE]') {
                void reader.cancel().catch(() => {})
                break readerLoop
              }
              try {
                const parsed = JSON.parse(data)

                if (parsed.type === 'thinking_start') {
                  setThinkingModeActive(true)
                  setThinkingOperations([])
                } else if (parsed.type === 'thinking_operation') {
                  setThinkingModeActive(true)
                  setThinkingOperations((prev) => [...prev, parsed.operation])
                } else if (parsed.type === 'thinking_operation_update') {
                  setThinkingOperations((prev) =>
                    prev.map((op) =>
                      op.id === parsed.operationId ? { ...op, ...parsed.updates } : op,
                    ),
                  )
                } else if (parsed.type === 'thinking_complete') {
                  setThinkingModeActive(false)
                } else if (parsed.type === 'followup_start') {
                  setIsTyping(true)
                }

                if (parsed.type === 'text' && parsed.content) {
                  setIsTyping(false)
                  assistantMessage += parsed.content
                  setStreamingMessages((prev) => {
                    const next = [...prev]
                    const last = next[next.length - 1]
                    if (last?.role === 'assistant') {
                      next[next.length - 1] = { ...last, content: assistantMessage }
                    }
                    return next
                  })
                }

                if (parsed.type === 'tool_call') {
                  const toolMsg: Message = {
                    role: 'tool',
                    content: parsed.result?.error ? (parsed.result.error as string) : '',
                    toolName: parsed.tool,
                    toolResult: parsed.result,
                    toolResults: [{ tool: parsed.tool, args: parsed.args, result: parsed.result }],
                    timestamp: new Date(),
                  }
                  setStreamingMessages((prev) => {
                    const next = [...prev]
                    const lastIdx = next.length - 1
                    if (next[lastIdx]?.role === 'assistant') {
                      next.splice(lastIdx, 0, toolMsg)
                    } else {
                      next.push(toolMsg)
                    }
                    return next
                  })
                }

                if (parsed.type === 'ask_question' && parsed.question && Array.isArray(parsed.options)) {
                  const askQuestion = {
                    questionId: String(parsed.questionId || `q-${Date.now()}`),
                    question: String(parsed.question),
                    options: parsed.options,
                    allowMultiple: !!parsed.allowMultiple,
                    allowOther: parsed.allowOther !== false,
                    replyTemplate: parsed.replyTemplate
                      ? String(parsed.replyTemplate)
                      : undefined,
                  }
                  setStreamingMessages((prev) => {
                    const next = [...prev]
                    const last = next[next.length - 1]
                    if (last?.role === 'assistant') {
                      next[next.length - 1] = { ...last, askQuestion }
                    }
                    return next
                  })
                }

                if ((parsed.type === 'suggest_actions' || parsed.type === 'action_choices') && parsed.choices) {
                  setStreamingMessages((prev) => {
                    const next = [...prev]
                    const last = next[next.length - 1]
                    if (last?.role === 'assistant') {
                      next[next.length - 1] = { ...last, actionChoices: parsed.choices }
                    }
                    return next
                  })
                }
              } catch {
                /* ignore parse errors */
              }
            }
          }
        }

        if (kind === 'draft_comment') {
          setStreamingMessages((prev) => {
            const next = [...prev]
            const lastIdx = next.length - 1
            const last = next[lastIdx]
            if (last?.role !== 'assistant') return prev
            const askUnanswered = Boolean(last.askQuestion && !last.askQuestion.answered)
            next[lastIdx] = {
              ...last,
              actionChoices: attachFillCommentChoice(last.actionChoices, askUnanswered),
            }
            return next
          })
        }
      } catch (err) {
        if (abortController.signal.aborted) return
        if (isChatStreamTransportError(err) && assistantMessage.trim()) {
          toast.message(CHAT_STREAM_REPLY_KEPT_TOAST.title, {
            description: CHAT_STREAM_REPLY_KEPT_TOAST.description,
          })
          return
        }
        toast.error(err instanceof Error ? err.message : 'Action failed')
        setStreamingMessages((prev) => {
          const next = [...prev]
          const last = next[next.length - 1]
          if (last?.role === 'assistant') {
            next[next.length - 1] = {
              ...last,
              content: `Failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
            }
          }
          return next
        })
      } finally {
        setThinkingModeActive(false)
        setIsStreaming(false)
        setIsTyping(false)
      }
    }

    const handleTodoActionChat = (event: CustomEvent<TodoActionChatRequestDetail>) => {
      const detail = event.detail
      if (!detail?.threadId) return
      pendingTodoActionChat = detail
      if (hostedThreadIdRef.current === detail.threadId) {
        pendingTodoActionChat = null
        void runTodoActionChat(detail)
      }
    }

    window.addEventListener('todo-action-chat-request', handleTodoActionChat as unknown as EventListener)
    return () =>
      window.removeEventListener('todo-action-chat-request', handleTodoActionChat as unknown as EventListener)
  }, [
    collapsed,
    onToggleCollapse,
    setStreamingMessages,
    setThinkingModeActive,
    setThinkingOperations,
    setIsStreaming,
    setIsTyping,
    selectedProvider,
    selectedModel,
    workspaceScopePathsKey,
    abortControllerRef,
  ])

  // When agent panel binds the new- thread, start any pending action stream.
  useEffect(() => {
    const pending = pendingTodoActionChat
    if (!pending || !hostedThreadId || hostedThreadId !== pending.threadId) return
    pendingTodoActionChat = null
    window.dispatchEvent(
      new CustomEvent('chat-set-mode', { detail: { mode: 'plan', threadId: pending.threadId } }),
    )
    window.dispatchEvent(new CustomEvent('todo-action-chat-request', { detail: pending }))
  }, [hostedThreadId])
}

export function useScrollDetection(deps: {
  messagesContainerRef: React.RefObject<HTMLDivElement | null>
  setIsNearBottom: (near: boolean) => void
  setUserHasScrolled: (scrolled: boolean) => void
  /** Sync breakaway flag - must update before React re-render (progressive reveal is ~14ms). */
  userHasScrolledRef?: React.RefObject<boolean>
  /** When true, ignore scroll events (our own follow/pin scrolls) */
  isProgrammaticScrollRef?: React.RefObject<boolean>
  /** Bumps when message list layout may have collapsed (seed hydrate / sticky). */
  contentKey?: string | number
}) {
  const {
    messagesContainerRef,
    setIsNearBottom,
    setUserHasScrolled,
    userHasScrolledRef,
    isProgrammaticScrollRef,
    contentKey,
  } = deps

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    /** Follow / near-bottom band for stream pinning. */
    const NEAR_BOTTOM_PX = 160
    /** Hide Jump + clear breakaway once the viewport is effectively at the end. */
    const TRUE_BOTTOM_PX = 48

    const markScrolledAway = () => {
      if (userHasScrolledRef) userHasScrolledRef.current = true
      setUserHasScrolled(true)
      setIsNearBottom(false)
    }

    const markAtBottom = () => {
      if (userHasScrolledRef) userHasScrolledRef.current = false
      setUserHasScrolled(false)
      setIsNearBottom(true)
    }

    const distanceFromBottom = () => {
      const { scrollTop, scrollHeight, clientHeight } = container
      return scrollHeight - (scrollTop + clientHeight)
    }

    /** Clear Jump when already at bottom even if no scroll event fired (short thread / settle). */
    const syncJumpVisibilityFromDom = () => {
      if (isProgrammaticScrollRef?.current) return
      if (
        shouldHideJumpToBottom({
          scrollHeight: container.scrollHeight,
          clientHeight: container.clientHeight,
          scrollTop: container.scrollTop,
          trueBottomPx: TRUE_BOTTOM_PX,
        })
      ) {
        markAtBottom()
      }
    }

    /** Last non-programmatic scrollTop - detect upward motion even within near-bottom band. */
    let lastScrollTop = container.scrollTop

    const handleScroll = () => {
      if (isProgrammaticScrollRef?.current) {
        // Ignore position updates from our own pin; never undo an active breakaway.
        if (!userHasScrolledRef?.current) {
          setIsNearBottom(true);
        }
        lastScrollTop = container.scrollTop
        return;
      }

      const distance = distanceFromBottom();
      const nearBottom = distance < NEAR_BOTTOM_PX;
      // Any upward motion breaks follow - including small nudges still inside the
      // near-bottom band (scrollbar / keyboard / trackpad without wheel).
      const scrolledUp = container.scrollTop < lastScrollTop - 2
      lastScrollTop = container.scrollTop

      if (scrolledUp) {
        markScrolledAway();
      } else if (!nearBottom) {
        markScrolledAway();
      } else if (!userHasScrolledRef?.current) {
        // Only re-engage follow when already following and still near bottom
        markAtBottom();
      } else if (distance < TRUE_BOTTOM_PX) {
        // Truly at bottom after breakaway - hide Jump and re-engage follow.
        markAtBottom();
      } else {
        // Broken away and still inside the near-bottom band (e.g. ~80px up).
        // Keep Jump visible and follow off until Jump click or true bottom.
        setIsNearBottom(false);
      }
    };

    /** Clear programmatic pin flag so user breakaway is not suppressed. */
    const cancelProgrammaticPin = () => {
      if (isProgrammaticScrollRef) isProgrammaticScrollRef.current = false
    }

    // Wheel/touch must break follow synchronously - scroll position updates lose the race
    // against progressive-reveal followKey ticks (~14ms) that still see stale React state.
    // User breakaway always wins, even during an in-flight programmatic pin.
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) {
        cancelProgrammaticPin()
        markScrolledAway()
        return
      }
      if (e.deltaY > 0) {
        // Scrolling toward bottom - re-check after the browser applies scrollTop
        // (no scroll event if already clamped at max).
        requestAnimationFrame(syncJumpVisibilityFromDom)
      }
    }

    let touchStartY: number | null = null
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0]?.clientY ?? null
    }
    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartY == null) return
      const y = e.touches[0]?.clientY
      if (y == null) return
      // Finger moving down => content scrolls up => break follow
      if (y - touchStartY > 8) {
        cancelProgrammaticPin()
        markScrolledAway()
      } else if (touchStartY - y > 8) {
        // Finger moving up => content scrolls down toward bottom
        requestAnimationFrame(syncJumpVisibilityFromDom)
      }
    }

    container.addEventListener('scroll', handleScroll, { passive: true });
    container.addEventListener('wheel', handleWheel, { passive: true });
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });

    // Content height changes (stream end, sticky prompt) often leave the viewport at
    // the bottom without a scroll event - sync Jump visibility from layout.
    // Observe the inner column too: container clientHeight stays put when seed
    // collapse shrinks scrollHeight, so a container-only observer would miss it.
    let resizeObserver: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        syncJumpVisibilityFromDom()
      })
      resizeObserver.observe(container)
      const inner = container.firstElementChild
      if (inner) resizeObserver.observe(inner)
    }
    // Initial sync for already-at-bottom threads after remount / HMR.
    syncJumpVisibilityFromDom()

    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      resizeObserver?.disconnect()
    };
  }, [
    messagesContainerRef,
    setIsNearBottom,
    setUserHasScrolled,
    userHasScrolledRef,
    isProgrammaticScrollRef,
    contentKey,
  ]);
}

/** Keep the latest stream line in view unless the user scrolled away. */
export function useStreamFollowScroll(deps: {
  messagesContainerRef: React.RefObject<HTMLDivElement | null>
  isStreaming: boolean
  userHasScrolled: boolean
  /** Sync twin of userHasScrolled - wins over stale state between progressive chunks. */
  userHasScrolledRef?: React.RefObject<boolean>
  /** Bumps when message list / content changes during a stream */
  followKey: string | number
  isProgrammaticScrollRef: React.RefObject<boolean>
  /** Keep Jump-to-bottom UI in sync when safety-net breakaway fires without a scroll event. */
  setUserHasScrolled?: (scrolled: boolean) => void
  setIsNearBottom?: (near: boolean) => void
}) {
  const {
    messagesContainerRef,
    isStreaming,
    userHasScrolled,
    userHasScrolledRef,
    followKey,
    isProgrammaticScrollRef,
    setUserHasScrolled,
    setIsNearBottom,
  } = deps

  useEffect(() => {
    if (!isStreaming || userHasScrolled || userHasScrolledRef?.current) return
    const el = messagesContainerRef.current
    if (!el) return

    // Re-check in case wheel/touch breakaway fired since the last render.
    if (userHasScrolledRef?.current) return

    // Safety net: if the viewport is already away from the bottom, treat as
    // breakaway even when the ref lagged (scrollbar drag without wheel).
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    if (distanceFromBottom > 160) {
      if (userHasScrolledRef) userHasScrolledRef.current = true
      setUserHasScrolled?.(true)
      setIsNearBottom?.(false)
      return
    }

    isProgrammaticScrollRef.current = true
    el.scrollTop = el.scrollHeight

    // Breakaway may have fired synchronously during the scroll write.
    if (userHasScrolledRef?.current) {
      isProgrammaticScrollRef.current = false
      return
    }

    // Hold the flag across two frames so the scroll event from this write is ignored
    requestAnimationFrame(() => {
      if (userHasScrolledRef?.current) {
        isProgrammaticScrollRef.current = false
        return
      }
      requestAnimationFrame(() => {
        isProgrammaticScrollRef.current = false
      })
    })
  }, [
    followKey,
    isStreaming,
    userHasScrolled,
    userHasScrolledRef,
    messagesContainerRef,
    isProgrammaticScrollRef,
    setUserHasScrolled,
    setIsNearBottom,
  ])
}
